import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  Collapse,
  IconButton,
  Tooltip,
  Skeleton,
  Avatar,
  Badge,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Folder as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  AudioFile as AudioIcon,
  VideoLibrary as VideoIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  ContentCopy as DuplicateIcon,
  Visibility as ViewIcon,
  Gavel as ApproveIcon,
  RemoveCircleOutline as WithdrawIcon,
  VisibilityOff as HideIcon,
  CalendarMonth as CalendarIcon,
  Image as ImageIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './BundleManagementPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const BundleManagementPage = () => {
  const { getAccessToken } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [expandedBundles, setExpandedBundles] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, bundle: null });
  const [priceManuallySet, setPriceManuallySet] = useState(false);

  const [bundleForm, setBundleForm] = useState({
    title: '',
    description: '',
    price: '',
    type: 'MONTHLY',
    releaseDate: '',
    thumbnailUrl: '',
    isActive: true,
    assetIds: [],
    eligibleTeams: ['all']
  });

  const [filters, setFilters] = useState({
    search: '',
    type: 'ALL',
    status: 'ALL',
    approval: 'ALL'
  });

  // Set when approval is refused because content inside is still pending.
  const [approvalBlocked, setApprovalBlocked] = useState({ open: false, bundle: null, blockedBy: [] });
  const [approvingAssetIds, setApprovingAssetIds] = useState(new Set());
  const [approvingAll, setApprovingAll] = useState(false);

  const [assetFilters, setAssetFilters] = useState({
    search: '',
    type: 'ALL',
    category: 'ALL',
    tag: 'ALL',
    sortBy: 'newest'
  });

  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [teams, setTeams] = useState([]);

  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchBundles();
    fetchAvailableAssets();
    fetchCategories();
    fetchTags();
    fetchTeams();
  }, []);

  const fetchBundles = async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/bundles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBundles(response.data);
    } catch (error) {
      showNotification('Failed to fetch bundles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAssets = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableAssets(response.data);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/categories/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTags(response.data.filter(tag => tag.isActive));
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    }
  };

  const fetchTeams = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/em/v1/admin/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.status === 'success') {
        setTeams(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const uploadThumbnailFile = async (file) => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'bundle-thumbnails');

    const response = await axios.post(
      `${API_BASE_URL}/em/v1/admin/images/upload`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to upload thumbnail');
    }
    return response.data.data.imageUrl;
  };

  const handleCreate = () => {
    setIsEditMode(false);
    setSelectedBundle(null);
    resetForm();
    setPriceManuallySet(false);
    setAssetFilters({
      search: '',
      type: 'ALL',
      category: 'ALL',
      tag: 'ALL',
      sortBy: 'newest'
    });
    setModalOpen(true);
  };

  const handleEdit = (bundle) => {
    setIsEditMode(true);
    setSelectedBundle(bundle);
    setBundleForm({
      title: bundle.title,
      description: bundle.description || '',
      price: bundle.price,
      type: bundle.type,
      releaseDate: bundle.releaseDate ? bundle.releaseDate.split('T')[0] : '',
      thumbnailUrl: bundle.thumbnailUrl || '',
      isActive: bundle.isActive,
      assetIds: (bundle.assets || []).map(a => a.id),
      eligibleTeams: bundle.eligibleTeams || ['all']
    });
    setPriceManuallySet(true);
    setAssetFilters({
      search: '',
      type: 'ALL',
      category: 'ALL',
      tag: 'ALL',
      sortBy: 'newest'
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!bundleForm.title || (bundleForm.price === '' && bundleForm.assetIds.length === 0)) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }
    if (bundleForm.assetIds.length === 0) {
      showNotification('Please select at least one asset', 'error');
      return;
    }
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }

      let thumbnailUrl = bundleForm.thumbnailUrl || '';
      if (selectedThumbnail) {
        setThumbnailUploading(true);
        try {
          thumbnailUrl = await uploadThumbnailFile(selectedThumbnail);
        } finally {
          setThumbnailUploading(false);
        }
      }

      if (isEditMode && selectedBundle) {
        await axios.put(
          `${API_BASE_URL}/api/v1/content/bundles/${selectedBundle.id}`,
          { title: bundleForm.title, description: bundleForm.description, price: parseFloat(bundleForm.price), type: bundleForm.type, releaseDate: bundleForm.releaseDate || null, thumbnailUrl, isActive: bundleForm.isActive, eligibleTeams: bundleForm.eligibleTeams || [] },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // PUT (replace) — always send the full desired membership so
        // unchecked assets are detached from the bundle. Using POST here
        // was additive-only and silently ignored removals.
        await axios.put(
          `${API_BASE_URL}/api/v1/content/bundles/${selectedBundle.id}/assets`,
          bundleForm.assetIds,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification('Bundle updated successfully', 'success');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/v1/content/bundles`,
          { ...bundleForm, price: parseFloat(bundleForm.price), releaseDate: bundleForm.releaseDate || null, thumbnailUrl },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification('Bundle created successfully', 'success');
      }
      setModalOpen(false);
      setSelectedThumbnail(null);
      resetForm();
      fetchBundles();
    } catch (error) {
      handleApiError(error, isEditMode ? 'Update failed' : 'Create failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.bundle) return;
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      await axios.delete(
        `${API_BASE_URL}/api/v1/content/bundles/${deleteConfirm.bundle.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('Bundle deleted successfully', 'success');
      setDeleteConfirm({ open: false, bundle: null });
      fetchBundles();
    } catch (error) {
      handleApiError(error, 'Delete failed');
      setDeleteConfirm({ open: false, bundle: null });
    }
  };

  const toBundlePayload = (b) => ({
    title: b.title,
    description: b.description || '',
    price: b.price,
    type: b.type,
    releaseDate: b.releaseDate ? b.releaseDate.split('T')[0] : null,
    thumbnailUrl: b.thumbnailUrl || '',
    isActive: b.isActive,
    eligibleTeams: b.eligibleTeams || [],
  });

  const handleToggleActive = async (bundle) => {
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      await axios.put(
        `${API_BASE_URL}/api/v1/content/bundles/${bundle.id}`,
        { ...toBundlePayload(bundle), isActive: !bundle.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification(`Bundle ${!bundle.isActive ? 'activated' : 'deactivated'}`, 'success');
      fetchBundles();
    } catch (error) {
      handleApiError(error, 'Status update failed');
    }
  };

  /**
   * Approval is not the same as Active. Inactive only takes a bundle out of the store; approval
   * decides whether members can see it at all, including the ones who already bought it.
   * Approving is refused while any asset inside is still pending, and the API says which.
   */
  const handleToggleApproval = async (bundle) => {
    const approved = bundle.approved !== true;
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/content/admin/approval/bundles/${bundle.id}?approved=${approved}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification(
        approved ? `"${bundle.title}" is now visible to members` : `"${bundle.title}" withdrawn from members`,
        'success'
      );
      if (response.data?.data?.blockedBy?.length) {
        setApprovalBlocked({ open: true, bundle, blockedBy: response.data.data.blockedBy });
      }
      fetchBundles();
    } catch (error) {
      const blockedBy = error.response?.data?.data?.blockedBy;
      if (error.response?.status === 409 && blockedBy?.length) {
        setApprovalBlocked({ open: true, bundle, blockedBy });
        return;
      }
      handleApiError(error, 'Could not change approval');
    }
  };

  /** Approve one blocking asset from inside the refusal dialog. */
  const handleApproveBlockingAsset = async (asset) => {
    setApprovingAssetIds(prev => new Set(prev).add(asset.id));
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      await axios.post(
        `${API_BASE_URL}/api/v1/content/admin/approval/assets/${asset.id}?approved=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApprovalBlocked(prev => ({
        ...prev,
        blockedBy: prev.blockedBy.filter(item => item.id !== asset.id),
      }));
      showNotification(`"${asset.title}" approved`, 'success');
      fetchAvailableAssets();
    } catch (error) {
      handleApiError(error, 'Could not approve the content');
    } finally {
      setApprovingAssetIds(prev => {
        const next = new Set(prev);
        next.delete(asset.id);
        return next;
      });
    }
  };

  /**
   * Approve everything still blocking this bundle and the bundle itself, in one call. The API
   * applies assets before bundles, so the bundle is no longer blocked by the time it is reached.
   */
  const handleApproveAllAndPublish = async () => {
    const { bundle, blockedBy } = approvalBlocked;
    setApprovingAll(true);
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      await axios.post(
        `${API_BASE_URL}/api/v1/content/admin/approval/bulk`,
        { assetIds: blockedBy.map(item => item.id), bundleIds: [bundle.id], approved: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification(`"${bundle.title}" and its content are now visible to members`, 'success');
      setApprovalBlocked({ open: false, bundle: null, blockedBy: [] });
      fetchBundles();
      fetchAvailableAssets();
    } catch (error) {
      handleApiError(error, 'Could not approve the bundle');
    } finally {
      setApprovingAll(false);
    }
  };

  const calcAssetTotal = (assetIds) => {
    return assetIds.reduce((sum, id) => {
      const asset = availableAssets.find(a => a.id === id);
      return sum + (asset?.price || 0);
    }, 0);
  };

  const handleTeamSelection = (e) => {
    const { value, checked } = e.target;
    setBundleForm(prev => {
      if (value === 'all') {
        return { ...prev, eligibleTeams: checked ? ['all'] : [] };
      }
      const filtered = prev.eligibleTeams.filter(t => t !== 'all');
      return {
        ...prev,
        eligibleTeams: checked ? [...filtered, value] : filtered.filter(t => t !== value)
      };
    });
  };

  const handleAssetToggle = (assetId) => {
    const currentIndex = bundleForm.assetIds.indexOf(assetId);
    const newAssetIds = [...bundleForm.assetIds];
    if (currentIndex === -1) { newAssetIds.push(assetId); } else { newAssetIds.splice(currentIndex, 1); }
    const updates = { ...bundleForm, assetIds: newAssetIds };
    if (!priceManuallySet) {
      updates.price = calcAssetTotal(newAssetIds);
    }
    setBundleForm(updates);
  };

  const toggleBundleExpand = (bundleId) => {
    setExpandedBundles(prev => ({ ...prev, [bundleId]: !prev[bundleId] }));
  };

  const resetForm = () => {
    setBundleForm({ title: '', description: '', price: '', type: 'MONTHLY', releaseDate: '', thumbnailUrl: '', isActive: true, assetIds: [], eligibleTeams: ['all'] });
    setPriceManuallySet(false);
    setSelectedThumbnail(null);
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleApiError = (error, defaultMessage) => {
    if (error.response?.status === 401) showNotification('Session expired. Please login again.', 'error');
    else if (error.response?.status === 403) showNotification('No permission for this action.', 'error');
    else if (error.response?.data?.message) showNotification(error.response.data.message, 'error');
    else showNotification(defaultMessage, 'error');
  };

  const getBundleTypeColor = (type) => {
    switch (type) {
      case 'MONTHLY': return 'primary';
      case 'THEMED': return 'secondary';
      case 'SERIES': return 'success';
      default: return 'default';
    }
  };

  const getBundleTypeGradient = (type) => {
    switch (type) {
      case 'MONTHLY': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'THEMED': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'SERIES': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
      default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  };

  const filteredBundles = bundles.filter(bundle => {
    if (filters.search && !bundle.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.type !== 'ALL' && bundle.type !== filters.type) return false;
    if (filters.status === 'ACTIVE' && !bundle.isActive) return false;
    if (filters.status === 'INACTIVE' && bundle.isActive) return false;
    if (filters.approval === 'APPROVED' && bundle.approved !== true) return false;
    if (filters.approval === 'PENDING' && bundle.approved === true) return false;
    return true;
  });

  const filteredDialogAssets = availableAssets
    .filter(asset => {
      // Search filter
      if (assetFilters.search && !asset.title.toLowerCase().includes(assetFilters.search.toLowerCase())) {
        return false;
      }
      // Type filter
      if (assetFilters.type !== 'ALL' && asset.type !== assetFilters.type) {
        return false;
      }
      // Category filter
      if (assetFilters.category !== 'ALL') {
        const categoryId = parseInt(assetFilters.category);
        // Handle both nested category object and categoryId field
        const assetCategoryId = asset.category?.id || asset.categoryId;
        if (assetCategoryId !== categoryId) {
          return false;
        }
      }
      // Tag filter
      if (assetFilters.tag !== 'ALL') {
        const tagId = parseInt(assetFilters.tag);
        const hasTag = asset.tags && asset.tags.some(t => t.id === tagId);
        if (!hasTag) {
          return false;
        }
      }
      // Only show READY assets
      if (asset.status !== 'READY') {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch(assetFilters.sortBy) {
        case 'newest':
          return new Date(b.createdAt || b.uploadedAt || 0) - new Date(a.createdAt || a.uploadedAt || 0);
        case 'oldest':
          return new Date(a.createdAt || a.uploadedAt || 0) - new Date(b.createdAt || b.uploadedAt || 0);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'price-high':
          return (b.price || 0) - (a.price || 0);
        case 'price-low':
          return (a.price || 0) - (b.price || 0);
        default:
          return 0;
      }
    });

  const stats = {
    total: bundles.length,
    active: bundles.filter(b => b.isActive).length,
    totalAssets: bundles.reduce((sum, b) => sum + (b.assetCount || 0), 0),
    totalRevenue: bundles.reduce((sum, b) => sum + (b.price || 0), 0),
  };

  const selectedAssetTotal = calcAssetTotal(bundleForm.assetIds);

  return (
    <Box className="bundle-management-page">
      {/* Header */}
      <Box className="page-header">
        <Box>
          <Typography variant="h4" fontWeight={700}>Bundle Management</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Create and manage content bundles for your users
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchBundles} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}>
            Create Bundle
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Bundles', value: stats.total, icon: <FolderIcon />, color: '#667eea' },
          { label: 'Active', value: stats.active, icon: <ActiveIcon />, color: '#4caf50' },
          { label: 'Total Assets', value: stats.totalAssets, icon: <InventoryIcon />, color: '#ff9800' },
          { label: 'Combined Value', value: `$${stats.totalRevenue.toFixed(2)}`, icon: <MoneyIcon />, color: '#e91e63' },
        ].map((stat, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Paper sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
              <Avatar sx={{ bgcolor: stat.color + '20', color: stat.color, width: 48, height: 48 }}>
                {stat.icon}
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>{stat.label}</Typography>
                <Typography variant="h5" fontWeight={700}>{stat.value}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth size="small"
              placeholder="Search bundles by name..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} label="Type" sx={{ borderRadius: 2 }}>
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
                <MenuItem value="THEMED">Themed</MenuItem>
                <MenuItem value="SERIES">Series</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} label="Status" sx={{ borderRadius: 2 }}>
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Approval</InputLabel>
              <Select value={filters.approval} onChange={(e) => setFilters({ ...filters, approval: e.target.value })} label="Approval" sx={{ borderRadius: 2 }}>
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="PENDING">Pending approval</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: { xs: 'left', md: 'center' } }}>
              <strong>{filteredBundles.length}</strong> result{filteredBundles.length !== 1 && 's'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Bundle Cards */}
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : filteredBundles.length === 0 ? (
        <Paper sx={{ py: 8, textAlign: 'center', borderRadius: 3, border: '2px dashed', borderColor: 'divider' }}>
          <FolderIcon sx={{ fontSize: 72, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No bundles found</Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>
            {filters.search || filters.type !== 'ALL' || filters.status !== 'ALL'
              ? 'Try adjusting your filters'
              : 'Create your first bundle to get started'}
          </Typography>
          {!filters.search && filters.type === 'ALL' && filters.status === 'ALL' && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreate} sx={{ borderRadius: 2 }}>
              Create First Bundle
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredBundles.map((bundle) => (
            <Grid item xs={12} md={6} lg={4} key={bundle.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, overflow: 'hidden', transition: 'all 0.3s', border: '1px solid', borderColor: 'divider', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.12)' } }}>
                {/* Card Header with gradient or thumbnail */}
                <Box sx={{ position: 'relative', height: 140, background: bundle.thumbnailUrl ? 'none' : getBundleTypeGradient(bundle.type), overflow: 'hidden' }}>
                  {bundle.thumbnailUrl ? (
                    <CardMedia component="img" height="140" image={bundle.thumbnailUrl} alt={bundle.title} sx={{ objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <FolderIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.6)' }} />
                    </Box>
                  )}
                  <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 1 }}>
                    <Chip label={bundle.type} size="small" sx={{ bgcolor: 'rgba(0,0,0,0.6)', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                  </Box>
                  <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.5 }}>
                    <Tooltip title={bundle.approved
                      ? `Approved${bundle.approvedBy ? ` by ${bundle.approvedBy}` : ''} — click to withdraw from members`
                      : 'Pending approval — click to approve. Everything inside must be approved first.'}>
                      <IconButton size="small" onClick={() => handleToggleApproval(bundle)} sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                        {bundle.approved ? <WithdrawIcon fontSize="small" /> : <ApproveIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={bundle.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'}>
                      <IconButton size="small" onClick={() => handleToggleActive(bundle)} sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                        {bundle.isActive ? <ViewIcon fontSize="small" /> : <HideIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ position: 'absolute', bottom: 10, right: 10, display: 'flex', gap: 0.5 }}>
                    <Chip
                      label={bundle.approved ? 'Approved' : 'Pending'}
                      size="small"
                      sx={{ bgcolor: bundle.approved ? 'rgba(46,125,50,0.9)' : 'rgba(237,108,2,0.9)', color: 'white', fontWeight: 600, fontSize: '0.7rem' }}
                    />
                    <Chip
                      label={bundle.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{ bgcolor: bundle.isActive ? 'rgba(76,175,80,0.9)' : 'rgba(158,158,158,0.9)', color: 'white', fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>

                <CardContent sx={{ flex: 1, p: 2.5 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom noWrap>{bundle.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: '2.5em' }}>
                    {bundle.description || 'No description provided'}
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Price</Typography>
                        <Typography variant="subtitle1" fontWeight={700} color="primary">${bundle.price?.toFixed(2)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Assets</Typography>
                        <Typography variant="subtitle1" fontWeight={700}>{bundle.assetCount || 0}</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {bundle.releaseDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                      <CalendarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(bundle.releaseDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}

                  {/* Eligible Teams */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                    {(!bundle.eligibleTeams || bundle.eligibleTeams.length === 0 || bundle.eligibleTeams.includes('all')) ? (
                      <Chip label="All Teams" size="small" variant="outlined" color="primary" sx={{ fontSize: '0.7rem' }} />
                    ) : (
                      bundle.eligibleTeams.map(teamName => (
                        <Chip key={teamName} label={teamName} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      ))
                    )}
                  </Box>

                  {/* Expandable asset list */}
                  {(bundle.assetCount > 0 || bundle.assets?.length > 0) && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        size="small" fullWidth variant="text"
                        onClick={() => toggleBundleExpand(bundle.id)}
                        endIcon={expandedBundles[bundle.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ textTransform: 'none', color: 'text.secondary', justifyContent: 'space-between' }}
                      >
                        {expandedBundles[bundle.id] ? 'Hide' : 'View'} assets ({bundle.assetCount || bundle.assets?.length || 0})
                      </Button>
                      <Collapse in={expandedBundles[bundle.id]}>
                        <List dense sx={{ bgcolor: 'action.hover', borderRadius: 2, mt: 1, maxHeight: 200, overflow: 'auto' }}>
                          {(bundle.assets || []).map(asset => (
                            <ListItem key={asset.id} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                {asset.type === 'AUDIO' ? <AudioIcon fontSize="small" color="primary" /> : 
                                 asset.type === 'VIDEO' ? <VideoIcon fontSize="small" color="secondary" /> :
                                 asset.type === 'PDF' ? <PdfIcon fontSize="small" /> : <WordIcon fontSize="small" />}
                              </ListItemIcon>
                              <ListItemText
                                primary={asset.title}
                                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                secondary={asset.price ? `$${asset.price.toFixed(2)}` : null}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          ))}
                          {(!bundle.assets || bundle.assets.length === 0) && (
                            <ListItem><ListItemText primary="Asset details not loaded" primaryTypographyProps={{ variant: 'caption', color: 'text.disabled' }} /></ListItem>
                          )}
                        </List>
                      </Collapse>
                    </Box>
                  )}
                </CardContent>

                <Divider />
                <CardActions sx={{ p: 1.5, justifyContent: 'flex-end', gap: 0.5 }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(bundle)} sx={{ textTransform: 'none', borderRadius: 2 }}>
                    Edit
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteConfirm({ open: true, bundle })} sx={{ textTransform: 'none', borderRadius: 2 }}>
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => { setModalOpen(false); setSelectedThumbnail(null); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {isEditMode ? 'Edit Bundle' : 'Create New Bundle'}
          <Typography variant="body2" color="text.secondary">
            {isEditMode ? 'Update bundle details and assets' : 'Fill in the details to create a new content bundle'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField fullWidth label="Title *" value={bundleForm.title} onChange={(e) => setBundleForm({ ...bundleForm, title: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" multiline rows={3} value={bundleForm.description} onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Type *</InputLabel>
                  <Select value={bundleForm.type} onChange={(e) => setBundleForm({ ...bundleForm, type: e.target.value })} label="Type *" sx={{ borderRadius: 2 }}>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                    <MenuItem value="THEMED">Themed</MenuItem>
                    <MenuItem value="SERIES">Series</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth label={priceManuallySet ? "Price * (custom)" : "Price * (auto)"} type="number"
                  value={bundleForm.price}
                  onChange={(e) => { setPriceManuallySet(true); setBundleForm({ ...bundleForm, price: e.target.value }); }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: priceManuallySet && bundleForm.assetIds.length > 0 ? (
                      <InputAdornment position="end">
                        <Tooltip title="Reset to auto-calculated price">
                          <IconButton size="small" onClick={() => { setPriceManuallySet(false); setBundleForm({ ...bundleForm, price: calcAssetTotal(bundleForm.assetIds) }); }}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null
                  }}
                  helperText={!priceManuallySet ? 'Auto-calculated from selected assets' : 'Manually set — click ↻ to reset'}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Release Date" type="date" value={bundleForm.releaseDate} onChange={(e) => setBundleForm({ ...bundleForm, releaseDate: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} md={8}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    Thumbnail Image (optional)
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<ImageIcon />}
                    disabled={thumbnailUploading}
                    fullWidth
                    sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2 }}
                  >
                    {selectedThumbnail ? selectedThumbnail.name : 'Choose File'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setSelectedThumbnail(file);
                          setBundleForm(prev => ({ ...prev, thumbnailUrl: '' }));
                        }
                      }}
                    />
                  </Button>
                  {selectedThumbnail && (
                    <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                      <img
                        src={URL.createObjectURL(selectedThumbnail)}
                        alt="Thumbnail preview"
                        style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 8, objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setSelectedThumbnail(null)}
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'error.light', color: 'white' } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  {!selectedThumbnail && isEditMode && bundleForm.thumbnailUrl && (
                    <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Current thumbnail:
                      </Typography>
                      <img
                        src={bundleForm.thumbnailUrl}
                        alt="Current thumbnail"
                        style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 8, objectFit: 'cover' }}
                      />
                    </Box>
                  )}
                  {!selectedThumbnail && (
                    <TextField
                      fullWidth
                      size="small"
                      label="Or paste URL"
                      value={bundleForm.thumbnailUrl}
                      onChange={(e) => setBundleForm({ ...bundleForm, thumbnailUrl: e.target.value })}
                      placeholder="https://example.com/thumbnail.jpg"
                      sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><ImageIcon color="action" /></InputAdornment>
                      }}
                    />
                  )}
                  {thumbnailUploading && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress sx={{ borderRadius: 1 }} />
                      <Typography variant="caption" color="text.secondary">Uploading thumbnail...</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={<Switch checked={bundleForm.isActive} onChange={(e) => setBundleForm({ ...bundleForm, isActive: e.target.checked })} color="success" />}
                  label={<Typography variant="body2" fontWeight={500}>{bundleForm.isActive ? 'Active' : 'Inactive'}</Typography>}
                  sx={{ mt: 1 }}
                />
              </Grid>

              {/* Eligible Teams */}
              <Grid item xs={12}>
                <Box sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Eligible Teams</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          value="all"
                          checked={bundleForm.eligibleTeams.includes('all')}
                          onChange={handleTeamSelection}
                        />
                      }
                      label={<Typography variant="body2">All Teams</Typography>}
                    />
                    {teams.map(team => (
                      <FormControlLabel
                        key={team.id || team.name}
                        control={
                          <Checkbox
                            size="small"
                            value={team.name}
                            checked={bundleForm.eligibleTeams.includes(team.name)}
                            onChange={handleTeamSelection}
                            disabled={bundleForm.eligibleTeams.includes('all')}
                          />
                        }
                        label={<Typography variant="body2">{team.name}</Typography>}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>

              {/* Asset Selection */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box mb={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>Select Assets *</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {bundleForm.assetIds.length} selected
                        {selectedAssetTotal > 0 && (
                          <> &middot; Asset total: ${selectedAssetTotal.toFixed(2)}
                            {priceManuallySet && parseFloat(bundleForm.price || 0) < selectedAssetTotal && (
                              <> &middot; <strong style={{ color: '#4caf50' }}>Savings: ${(selectedAssetTotal - parseFloat(bundleForm.price || 0)).toFixed(2)}</strong></>
                            )}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Asset Filters */}
                  <Grid container spacing={1.5} mb={1.5}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by title..."
                        value={assetFilters.search}
                        onChange={(e) => setAssetFilters({ ...assetFilters, search: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={assetFilters.type}
                          onChange={(e) => setAssetFilters({ ...assetFilters, type: e.target.value })}
                          label="Type"
                        >
                          <MenuItem value="ALL">All Types</MenuItem>
                          <MenuItem value="AUDIO">Audio</MenuItem>
                          <MenuItem value="VIDEO">Video</MenuItem>
                          <MenuItem value="PDF">PDF</MenuItem>
                          <MenuItem value="WORD">Word</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={assetFilters.category}
                          onChange={(e) => setAssetFilters({ ...assetFilters, category: e.target.value })}
                          label="Category"
                        >
                          <MenuItem value="ALL">All Categories</MenuItem>
                          {categories.map(cat => (
                            <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tag</InputLabel>
                        <Select
                          value={assetFilters.tag}
                          onChange={(e) => setAssetFilters({ ...assetFilters, tag: e.target.value })}
                          label="Tag"
                        >
                          <MenuItem value="ALL">All Tags</MenuItem>
                          {tags.map(tag => (
                            <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sort By</InputLabel>
                        <Select
                          value={assetFilters.sortBy}
                          onChange={(e) => setAssetFilters({ ...assetFilters, sortBy: e.target.value })}
                          label="Sort By"
                        >
                          <MenuItem value="newest">Newest First</MenuItem>
                          <MenuItem value="oldest">Oldest First</MenuItem>
                          <MenuItem value="title">Title (A-Z)</MenuItem>
                          <MenuItem value="price-high">Price (High to Low)</MenuItem>
                          <MenuItem value="price-low">Price (Low to High)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  
                  {/* Results count */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Showing {filteredDialogAssets.length} of {availableAssets.filter(a => a.status === 'READY').length} available assets
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', borderRadius: 2 }}>
                  <List disablePadding>
                    {filteredDialogAssets.length === 0 ? (
                      <ListItem><ListItemText primary="No assets available" primaryTypographyProps={{ color: 'text.disabled' }} /></ListItem>
                    ) : (
                      filteredDialogAssets.map((asset) => {
                        const checked = bundleForm.assetIds.indexOf(asset.id) !== -1;
                        return (
                          <ListItem key={asset.id} disablePadding sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                            <ListItemButton onClick={() => handleAssetToggle(asset.id)} sx={{ bgcolor: checked ? 'primary.50' : 'transparent', '&:hover': { bgcolor: checked ? 'primary.100' : 'action.hover' } }}>
                              <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple color="primary" />
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {asset.type === 'AUDIO' ? <AudioIcon color="primary" fontSize="small" /> : 
                               asset.type === 'VIDEO' ? <VideoIcon color="secondary" fontSize="small" /> :
                               asset.type === 'PDF' ? <PdfIcon fontSize="small" /> : <WordIcon fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText
                              primary={asset.title}
                              secondary={`${asset.type} — $${asset.price?.toFixed(2) || '0.00'}`}
                              primaryTypographyProps={{ fontWeight: checked ? 600 : 400 }}
                            />
                            {asset.bundles && asset.bundles.length > 0 && (
                              <Tooltip title={asset.bundles.map(b => b.name).join(', ')}>
                                <Chip
                                  label={`${asset.bundles.length} bundle${asset.bundles.length > 1 ? 's' : ''}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', mr: 0.5 }}
                                />
                              </Tooltip>
                            )}
                            <Chip label={asset.type} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                            {asset.approved !== true && (
                              <Tooltip title="Pending approval — members cannot see this yet, and a bundle containing it cannot be approved">
                                <Chip label="Pending" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              </Tooltip>
                            )}
                            </ListItemButton>
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => { setModalOpen(false); setSelectedThumbnail(null); }} sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4 }}>
            {isEditMode ? 'Update Bundle' : 'Create Bundle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval refused: the contents have to be signed off first */}
      <Dialog
        open={approvalBlocked.open}
        onClose={() => setApprovalBlocked({ open: false, bundle: null, blockedBy: [] })}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 520 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Approve the content first
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            "{approvalBlocked.bundle?.title}" cannot go out to members while the content inside it is
            still waiting for approval. Approve it here, one at a time or all at once.
          </Typography>
          <List dense sx={{ mt: 1 }}>
            {approvalBlocked.blockedBy.map((item) => (
              <ListItem
                key={item.id}
                divider
                secondaryAction={
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    disabled={approvingAssetIds.has(item.id) || approvingAll}
                    onClick={() => handleApproveBlockingAsset(item)}
                    startIcon={approvingAssetIds.has(item.id) ? <CircularProgress size={14} /> : <ApproveIcon />}
                  >
                    Approve
                  </Button>
                }
              >
                <ListItemText
                  primary={item.title}
                  secondary={item.type}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>
          {approvalBlocked.blockedBy.length === 0 && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Everything inside is approved now — approve the bundle to publish it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setApprovalBlocked({ open: false, bundle: null, blockedBy: [] })}>
            Close
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={approvingAll}
            startIcon={approvingAll ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />}
            onClick={handleApproveAllAndPublish}
          >
            {approvalBlocked.blockedBy.length === 0
              ? 'Publish bundle'
              : `Approve all ${approvalBlocked.blockedBy.length} and publish bundle`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, bundle: null })} PaperProps={{ sx: { borderRadius: 3, maxWidth: 440 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Delete Bundle
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={1}>
            Are you sure you want to delete <strong>"{deleteConfirm.bundle?.title}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The assets within this bundle will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, bundle: null })} sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BundleManagementPage;
